import React, {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const SelectContext = createContext(null);

const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('Select components must be used within a <Select>');
  }
  return context;
};

const getTextFromChildren = (children) => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map((child) => getTextFromChildren(child)).join(' ');
  }

  if (isValidElement(children)) {
    return getTextFromChildren(children.props.children);
  }

  return '';
};

export const Select = ({
  children,
  className = '',
  value,
  defaultValue = '',
  onValueChange,
  disabled = false,
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const [selectedLabel, setSelectedLabel] = useState('');

  const isControlled = value !== undefined && value !== null;
  const currentValue = isControlled ? value : internalValue;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (currentValue === '' || currentValue === undefined || currentValue === null) {
      setSelectedLabel('');
    }
  }, [currentValue]);

  const handleSelect = useCallback(
    (newValue, label) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }

      setSelectedLabel(label);

      if (onValueChange) {
        onValueChange(newValue);
      }

      setIsOpen(false);
    },
    [isControlled, onValueChange]
  );

  const contextValue = useMemo(
    () => ({
      open: isOpen,
      value: currentValue ?? '',
      disabled,
      selectedLabel,
      setSelectedLabel,
      toggleOpen: () => {
        if (!disabled) {
          setIsOpen((prev) => !prev);
        }
      },
      close: () => setIsOpen(false),
      onItemSelect: handleSelect,
    }),
    [isOpen, currentValue, disabled, selectedLabel, handleSelect]
  );

  return (
    <SelectContext.Provider value={contextValue}>
      <div ref={containerRef} className={`relative ${className}`}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

export const SelectTrigger = ({ children, className = '', ...props }) => {
  const { toggleOpen, open, disabled } = useSelectContext();

  return (
    <button
      type="button"
      className={`flex w-full items-center justify-between rounded-md border border-gris-piedra/40 bg-white px-3 py-2 text-sm text-azul-oscuro shadow-sm transition focus:outline-none focus:ring-2 focus:ring-verde-esmeralda/60 ${
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      } ${className}`}
      onClick={toggleOpen}
      aria-haspopup="listbox"
      aria-expanded={open}
      disabled={disabled}
      {...props}
    >
      <div className="flex-1 text-left">{children}</div>
      <svg
        className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : 'rotate-0'}`}
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.5 7.5L10 12L14.5 7.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

export const SelectContent = ({ children, className = '' }) => {
  const { open, onItemSelect } = useSelectContext();

  if (!open) {
    return null;
  }

  const enhancedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) {
      return child;
    }

    return cloneElement(child, {
      onSelect: onItemSelect,
    });
  });

  return (
    <div className={`absolute z-50 mt-2 w-full overflow-hidden rounded-md border border-gris-piedra/30 bg-white shadow-lg ${className}`}>
      <div className="max-h-60 overflow-y-auto py-1">{enhancedChildren}</div>
    </div>
  );
};

export const SelectItem = ({ children, className = '', value, onSelect, ...props }) => {
  const { value: selectedValue, setSelectedLabel, close, disabled } = useSelectContext();
  const isSelected = String(selectedValue) === String(value);
  const label = useMemo(() => getTextFromChildren(children).trim(), [children]);

  useEffect(() => {
    if (isSelected) {
      setSelectedLabel(label);
    }
  }, [isSelected, label, setSelectedLabel]);

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    if (onSelect) {
      onSelect(String(value), label);
    }

    close();
  };

  return (
    <div
      role="option"
      aria-selected={isSelected}
      className={`flex cursor-pointer items-center px-3 py-2 text-sm text-azul-oscuro hover:bg-verde-esmeralda/10 ${
        isSelected ? 'bg-verde-esmeralda/10 font-medium text-verde-esmeralda' : ''
      } ${className}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  );
};

export const SelectValue = ({ placeholder = 'Seleccione una opción', className = '' }) => {
  const { value: selectedValue, selectedLabel } = useSelectContext();
  const hasValue = selectedValue !== undefined && selectedValue !== null && selectedValue !== '';

  return (
    <span className={`block truncate text-sm ${hasValue ? 'text-azul-oscuro' : 'text-gris-piedra'} ${className}`}>
      {hasValue ? selectedLabel || placeholder : placeholder}
    </span>
  );
};

export default Select;
